import * as React from "react"
import { type OAuthService } from "@digits-graphql/frontend/graphql-bearer"
import { useLogInOAuthMutation } from "@digits-graphql/frontend/graphql-public"
import useSession from "@digits-shared/hooks/useSession"
import useStateBoolean from "@digits-shared/hooks/useStateBoolean"
import { useSignIn } from "src/frontend/components/Public/hooks/useSignIn"
import { useOnboardingContext } from "src/frontend/components/Public/Onboarding/OnboardingContext"
import { sessionError } from "src/frontend/components/Session/Glow/error"
import { useChromeAlerts } from "src/frontend/hooks/useChromeAlerts"
import type FrontendSession from "src/frontend/session"

export function useOAuthSignIn(provider: OAuthService) {
  const session = useSession<FrontendSession>()
  const activeLegalEntityId = session.lastKnownLegalEntityId
  const { dispatch } = useOnboardingContext()
  const { onSignInCompleted, onSignInError } = useSignIn()
  const { value: isLoading, setTrue: setLoading, setFalse: unsetLoading } = useStateBoolean()
  const { alertError } = useChromeAlerts()

  const onCompleted = React.useCallback(
    (...args: Parameters<typeof onSignInCompleted>) => {
      unsetLoading()
      return onSignInCompleted(...args)
    },
    [unsetLoading, onSignInCompleted]
  )

  const onError = React.useCallback(
    (...args: Parameters<typeof onSignInError>) => {
      unsetLoading()
      return onSignInError(...args)
    },
    [unsetLoading, onSignInError]
  )

  const [logInOAuth] = useLogInOAuthMutation({
    onCompleted,
    onError,
    context: { publicAPI: true },
  })

  const onOAuthSuccess = React.useCallback(
    (code: string) => {
      dispatch({ type: "SET_LOADING", loading: true })
      return logInOAuth({
        variables: {
          code,
          service: provider,
          activeLegalEntityId,
        },
      })
    },
    [activeLegalEntityId, dispatch, logInOAuth, provider]
  )

  const onOAuthFailure = React.useCallback(
    (error: string, code?: string) => {
      const errorMessage = getFriendlyErrorMessage(error, code)
      alertError(errorMessage)
      dispatch({ type: "SET_ERROR_TEXT", text: errorMessage })
      dispatch({ type: "SET_LOADING", loading: false })
      unsetLoading()
    },
    [dispatch, unsetLoading, alertError]
  )

  return React.useMemo(
    () => ({
      isLoading,
      setLoading,
      unsetLoading,
      onSuccess: onOAuthSuccess,
      onFailure: onOAuthFailure,
    }),
    [onOAuthFailure, onOAuthSuccess, isLoading, setLoading, unsetLoading]
  )
}

export function getFriendlyErrorMessage(message: string, code: string | undefined) {
  switch (code) {
    case "popup_blocked_by_browser":
    case "popup_failed_to_open":
    case "idpiframe_initialization_failed":
      return message
    default:
      return sessionError
  }
}
